import { createContext, useContext, type ReactNode } from "react";

import type { PerformanceOut } from "@/api/performance";

interface PerformanceContextValue {
  data: PerformanceOut | null;
  isLoading: boolean;
}

/**
 * Shared performance query result available to all Performance widgets. The
 * dashboard page fetches once, then every widget reads slices of `flight_rows`
 * and `summary` from this context — no duplicate fetches.
 */
const PerformanceDataContext = createContext<PerformanceContextValue>({
  data: null,
  isLoading: false,
});

export function PerformanceDataProvider({
  data,
  isLoading,
  children,
}: {
  data: PerformanceOut | null;
  isLoading: boolean;
  children: ReactNode;
}) {
  return (
    <PerformanceDataContext.Provider value={{ data, isLoading }}>
      {children}
    </PerformanceDataContext.Provider>
  );
}

export function usePerformanceData(): PerformanceOut | null {
  return useContext(PerformanceDataContext).data;
}

export function usePerformanceLoading(): boolean {
  return useContext(PerformanceDataContext).isLoading;
}
